import { observer } from 'mobx-react-lite'
import { ReactElement } from 'react'
import { I18n } from 'common'
import { useUiStateStore } from '../../../../../../src/store/uiState/UiStateStore'
import styled from 'styled-components'
import { type ICartProductData } from 'cart'
import { breakpoints } from '../../../../../../src/theme/breakpoints'
import { defaultTheme } from '../../../../../../src/theme/index'
import { Price } from '../../../../../../src/ui-components/products/Price'
import { OtherPrices } from '../../../index'
import { StoreInstances } from '../../../../../../src/store/StoreInstancesContainer'
import { QuantityPriceBreak } from '../quantityPriceBreak'
import { show as showNiceModal } from '@ebay/nice-modal-react'
import { Skeleton } from '@mui/material'
import { BundleTooltip } from '../BundleTooltip'
import { useUserStore } from '../../../../../../src/store/user/UserStore'

interface Props {
  cartProduct: ICartProductData
}

const Prices = observer((props: Props): ReactElement => {
  const { cartProduct } = props

  const uiStore = useUiStateStore()
  const userStore = useUserStore()
  const { isCondensedView, showBundles } = uiStore
  const displayExclTax = userStore?.preferences?.displayExclTax === 'true'

  const {
    activeLocation,
    priceSpinner,
    yourCostAfterPriceBreaks,
    isZeroCost,
    updateQuantity,
    setCurrentQty,
    product,
  } = cartProduct

  const {
    list,
    coreCost,
    coreList,
    priceBreaks,
    otherPrices = [],
  } = activeLocation || {}

  const thumbnail = product.partImages?.length
    ? product.partImages.find((img) => img.type === 'thumbnail')?.url
    : ''

  // shouldn't call when qty is less than intialSelectorQty
  const updatePriceBreaks = async (qty: number) => {
    if (qty < activeLocation.displayMinQty) {
      StoreInstances.uiStore.displayErrorNotification(
        `Minimum quantity should be ${activeLocation.displayMinQty}`
      )
      return
    }

    await updateQuantity(qty, activeLocation)
    setCurrentQty(qty)
  }

  const handleClickQuantityPriceBreak = () => {
    showNiceModal(QuantityPriceBreak, {
      updatePriceBreaks,
      priceBreaks,
      image: thumbnail,
      partNumber: product.partNumber,
      description: product.description,
      lineCode: product.lineCode,
    })
  }

  return (
    <Cell>
      {isZeroCost ? (
        <Wrap className="fw-bold">
          <I18n name="callForPrice" />
        </Wrap>
      ) : (
        <PriceWrapper isCondensedView={isCondensedView}>
          {showBundles() &&
            product?.eligibleBundles &&
            product?.eligibleBundles?.length > 0 && (
              <BundleWrapper>
                <BundleTooltip bundles={product?.eligibleBundles} />
              </BundleWrapper>
            )}

          {priceSpinner && <Skeleton variant="rectangular" width={210} />}
          {!priceSpinner && uiStore.showYourCost() && (
            <YourCosts>
              <Flex>
                <PriceLabel bold>
                  <I18n name="yourCost" />
                </PriceLabel>
                <PriceValue>
                  <BigPrice isCondensedView={isCondensedView}>
                    <Price num={yourCostAfterPriceBreaks()} />
                  </BigPrice>
                </PriceValue>
              </Flex>
              {displayExclTax && (
                <Flex>
                  <PriceLabel />
                  <ExclTaxText>
                    <I18n name="displayExclTax" />
                  </ExclTaxText>
                </Flex>
              )}

              {coreCost > 0 && (
                <Flex>
                  <PriceLabel>
                    <I18n name="coreCost" />
                  </PriceLabel>
                  <PriceValue>
                    <Price num={coreCost} />
                  </PriceValue>
                </Flex>
              )}

              {priceBreaks?.length > 0 && !isCondensedView && (
                <LocalLinkPriceBreak
                  onClick={handleClickQuantityPriceBreak}
                  isCondensedView={isCondensedView}
                >
                  <NoWrap isCondensedView={isCondensedView}>
                    <I18n name="qtyPriceBreak" />
                  </NoWrap>
                </LocalLinkPriceBreak>
              )}
            </YourCosts>
          )}

          {priceSpinner && <Skeleton variant="rectangular" width={210} />}
          {!priceSpinner && (
            <ListPrices>
              <Flex>
                <PriceLabel bold>
                  <I18n name="listPrice" />
                </PriceLabel>
                <PriceValue>
                  <BigPrice isCondensedView={isCondensedView}>
                    <Price num={list} />
                  </BigPrice>
                </PriceValue>
              </Flex>

              {coreList > 0 && (
                <Flex>
                  <PriceLabel>
                    <I18n name="coreList" />
                  </PriceLabel>
                  <PriceValue>
                    <Price num={coreList} />
                  </PriceValue>
                </Flex>
              )}
            </ListPrices>
          )}

          {!priceSpinner &&
            otherPrices
              .filter(
                (item) =>
                  item.priceValue > 0 &&
                  item.priceCodeName?.toLowerCase() !== 'list'
              )
              .map((item) => (
                <OtherPrices
                  item={item}
                  key={`price_${item.priceCodeName?.toLowerCase()}`}
                  isCondensedView={isCondensedView}
                />
              ))}
        </PriceWrapper>
      )}
    </Cell>
  )
})

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  font-size: 16px;
  line-height: 24px;
  text-align: start;
`

interface CardProps {
  isCondensedView?: boolean
}

const Wrap = styled.span<CardProps>`
  white-space: normal;
  font-size: ${(p) => p.isCondensedView && '14px'};
`

const NoWrap = styled.span<CardProps>`
  white-space: nowrap;
  font-size: ${(p) => p.isCondensedView && '14px'};
`

const PriceWrapper = styled.div<CardProps>`
  display: flex;
  flex-direction: column;
  gap: ${(p) => (p.isCondensedView ? '0px' : '20px')};
  justify-content: flex-start;
  font-size: ${(p) => p.isCondensedView && '14px'};
  width: 100%;
`

const Flex = styled.div`
  display: flex;
`

interface PriceLabelProps {
  bold?: boolean
}

const PriceLabel = styled.span<PriceLabelProps>`
  text-align: left;
  flex: 1;
  font-weight: ${(p) => (p.bold ? 'bold' : 'normal')};
  text-wrap: nowrap;
`

const PriceValue = styled.span`
  flex: 1;
  text-align: left;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    margin-left: 0px;
  }
`

const YourCosts = styled.div``
const ListPrices = styled.div``

const ExclTaxText = styled.div`
  flex: 1;
  font-style: italic;
  font-weight: 400;
  font-size: 11px;
  color: ${defaultTheme.colors.columnBorder};
`

const LocalLink = styled.div`
  color: ${defaultTheme.colors.link};
  text-decoration: underline;
  cursor: pointer;
  margin: 10px 10px 0 0;
`

const LocalLinkPriceBreak = styled(LocalLink)<CardProps>`
  max-width: 78px;
  overflow-wrap: wrap;
  margin: ${(p) => p.isCondensedView && '0px'};
`

const BigPrice = styled.span<CardProps>`
  font-size: ${(p) => (p.isCondensedView ? '14px' : '20px')};
  font-weight: bold;
  font-family: Roboto, serif;
`
// const BundleTag = styled.div`
//   display: inline-flex;
//   align-items: center;
//   gap: 6px;
//   background-color: #f6e8b1;
//   color: #000;
//   font-weight: 500;
//   font-size: 14px;
//   padding: 4px 10px;
//   border-radius: 4px;
//   cursor: default;
//   width: fit-content;
//   transition: all 0.2s ease;
//   margin-bottom: 8px;

//   &:hover {
//     background-color: #000;
//     color: #f6e8b1;

//     svg {
//       color: #f6e8b1;
//     }
//   }
// `

const BundleWrapper = styled.div`
  display: inline-flex;
  align-self: flex-start;
`

export default Prices
